import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2 } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Logo } from "@/components/Logo";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password !== confirm) {
      setError("Passwords don't match");
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/customer/reset-password", { token, password });
      setDone(true);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Reset failed — request a new link");
    } finally {
      setBusy(false);
    }
  };

  const inputCls = "w-full rounded-xl border border-white/10 bg-[#0B0B0E] px-4 py-3 text-sm text-white placeholder-zinc-600 outline-none transition-colors focus:border-emerald-300/60";

  return (
    <div className="flex min-h-[70vh] items-center justify-center px-4 py-16" data-testid="reset-password-page">
      <div className="w-full max-w-sm rounded-3xl border border-white/10 bg-[#141419] p-8 text-center">
        <div className="flex justify-center"><Logo size={52} /></div>
        <h1 className="font-heading mt-5 text-2xl font-bold text-white">Reset Password</h1>
        {!token ? (
          <p className="mt-3 text-sm text-zinc-500" data-testid="reset-password-no-token">
            This reset link is missing its token. Use the link from your email.
          </p>
        ) : done ? (
          <div data-testid="reset-password-success">
            <CheckCircle2 size={40} className="mx-auto mt-6 text-emerald-300" />
            <p className="mt-4 text-sm text-zinc-400">Password updated. Sign in with your new password.</p>
            <Link
              to="/account"
              data-testid="reset-password-signin-link"
              className="mt-6 inline-block rounded-full bg-emerald-300 px-8 py-3.5 text-sm font-bold text-black transition-colors hover:bg-emerald-200"
            >
              Go to Sign In
            </Link>
          </div>
        ) : (
          <form onSubmit={submit} className="mt-7 space-y-3 text-left" data-testid="reset-password-form">
            <input
              type="password" required minLength={8} placeholder="New password (8+ characters)"
              value={password} onChange={(e) => setPassword(e.target.value)}
              className={inputCls} data-testid="reset-password-new" autoComplete="new-password"
            />
            <input
              type="password" required minLength={8} placeholder="Confirm new password"
              value={confirm} onChange={(e) => setConfirm(e.target.value)}
              className={inputCls} data-testid="reset-password-confirm" autoComplete="new-password"
            />
            {error && <p className="text-xs text-red-400" data-testid="reset-password-error">{error}</p>}
            <button
              type="submit" disabled={busy}
              data-testid="reset-password-submit"
              className="flex w-full items-center justify-center gap-2 rounded-full bg-emerald-300 py-3.5 text-sm font-bold text-black transition-colors hover:bg-emerald-200 disabled:opacity-50"
            >
              {busy && <Loader2 size={15} className="animate-spin" />} Set New Password
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
